import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { usePagedView, ViewToggle, Pagination } from "../components/Paged";
import { getInvoices, money, type Invoice } from "../lib/billing";

export function Invoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getInvoices()
      .then((r) => setInvoices(r.invoices))
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, []);
  const { view, page, pageCount, pageItems, setView, setPage } = usePagedView(invoices, 12, "list");

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader title="Invoices" subtitle="Your billing history for RecruitTrucker Pro." />

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : invoices.length === 0 ? (
        <div className="rounded-card border border-line bg-surface p-8 text-center">
          <p className="text-sm text-muted">No invoices yet.</p>
          <Link to="/billing" className="mt-2 inline-block font-mono text-sm font-bold text-accent-strong">Go to billing →</Link>
        </div>
      ) : (
        <>
          <div className="flex justify-end"><ViewToggle view={view} onChange={setView} /></div>
          {view === "list" ? (
            <div className="overflow-hidden rounded-card border border-line bg-surface">
              <div className="grid grid-cols-[110px_1fr_100px_90px_auto] items-center gap-4 border-b border-line px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-muted">
                <span>Date</span><span>Description</span><span className="justify-self-end">Amount</span><span>Status</span><span className="justify-self-end">PDF</span>
              </div>
              {pageItems.map((i) => (
                <div key={i.id} className="grid grid-cols-[110px_1fr_100px_90px_auto] items-center gap-4 border-t border-line px-5 py-4">
                  <span className="font-mono text-xs text-muted">{new Date(i.created * 1000).toLocaleDateString()}</span>
                  <span className="truncate text-sm">{i.description}{i.number ? ` · ${i.number}` : ""}</span>
                  <span className="justify-self-end font-mono text-sm font-bold">{money(i.amount, i.currency)}</span>
                  <StatusLabel status={i.status} />
                  <span className="justify-self-end">
                    {i.invoice_pdf ? <a href={i.invoice_pdf} target="_blank" rel="noreferrer" className="font-mono text-xs font-bold text-accent-strong">Download</a> : <span className="font-mono text-xs text-muted">—</span>}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {pageItems.map((i) => (
                <div key={i.id} className="grid gap-2 rounded-card border border-line bg-surface p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-sm font-bold">{money(i.amount, i.currency)}</span>
                    <StatusLabel status={i.status} />
                  </div>
                  <span className="truncate text-sm text-muted">{i.description}{i.number ? ` · ${i.number}` : ""}</span>
                  <span className="font-mono text-[10px] text-muted">{new Date(i.created * 1000).toLocaleDateString()}</span>
                  {i.invoice_pdf && <a href={i.invoice_pdf} target="_blank" rel="noreferrer" className="font-mono text-xs font-bold text-accent-strong">Download PDF →</a>}
                </div>
              ))}
            </div>
          )}
          <Pagination page={page} pageCount={pageCount} onPage={setPage} />
        </>
      )}
    </div>
  );
}
